/*
 * ProdukteAnlegen.tsx
 *
 * Created by Dr. Maximillian Dornseif 2021 in huWaWi3
 * Copyright (c) 2021 HUDORA GmbH
 */

import { gql, useMutation, useQuery } from '@apollo/client'
import { PrimaryButton } from '@fluentui/react/lib/Button'
import { SpinButton } from '@fluentui/react/lib/SpinButton'
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner'
import { Stack } from '@fluentui/react/lib/Stack'
import { TextField } from '@fluentui/react/lib/TextField'
import React, { useCallback, useState } from 'react'

import { ITEM_FRAGMENT, SKUM_FRAGMENT, SKU_FRAGMENT } from '../fragments'
import { LadeChecker } from '../lib/components/UiChecker'
import { DisplayItems, PRODUKT_WITH_ITEM_QUERY } from './EapProdukteApp'

const NEWITEM_MUTATION = gql`
  mutation NewItem($nr: ID!, $name: String!, $kartons: Int!, $searchStart: Int!) {
    h3ProduktAddItem(designator: $nr, name: $name, kartons: $kartons, startSearch: $searchStart) {
      id
      itemList {
        ...itemFragment
        skuMasterList {
          ...skuMasterFragment
          skuList {
            ...skuFragment
          }
        }
      }
    }
  }
  ${ITEM_FRAGMENT}
  ${SKUM_FRAGMENT}
  ${SKU_FRAGMENT}
`
export function NeuesItemAnlegen(props: { entity: Record<string, any> }) {
  const { loading, data, error } = useQuery(PRODUKT_WITH_ITEM_QUERY, {
    variables: { nr: props.entity.designator },
  })
  const entity = data?.entity

  if (loading || error) {
    return <LadeChecker loading={loading} error={error} />
  }
  return <NeuesItemAnlegenInner {...props} entity={entity} />
}

function NeuesItemAnlegenInner(props: { entity: Record<string, any> }) {
  const items = (props.entity?.itemList || []).map((x) => x.designator)
  const defaultSuche = Math.min(...(items || []).map((x) => Number(x))) || 30000
  const [name, setName] = useState('neu')
  const [kartons, setKartons] = useState<number>(1)
  const [searchStart, setSearchStart] = useState<number>(isFinite(defaultSuche) ? defaultSuche : 30000)
  const [uebertragen, { loading: saving }] = useMutation(NEWITEM_MUTATION, {
    variables: {
      nr: props?.entity?.designator,
      name: null,
      kartons: null,
      searchStart: null,
    },
  })

  const anlegenStarten = useCallback(
    (ev: any) => {
      const variables = {
        nr: props?.entity?.designator,
        name,
        kartons: Number(kartons),
        searchStart: Number(searchStart || 30000),
      }
      uebertragen({
        variables,
      })
      ev.preventDefault()
    },
    [kartons, name, props?.entity?.designator, searchStart]
  )

  const onChange = React.useCallback(
    (event: React.SyntheticEvent<HTMLElement>, newValue?: string) => {
      if (newValue !== undefined) {
        // In reality this might have some additional validation or other special handling
        setKartons(Number(newValue))
      }
    },
    [kartons]
  )

  if (!props?.entity) {
    return null
  }

  items.sort()

  return (
    <Stack tokens={{ childrenGap: '1em' }}>
      <div>
        Hier können Sie zum Produkt {props.entity.designator} ein neues Item (Auftragsartikel) anlegen. {name}{' '}
        {kartons} {searchStart}
      </div>
      <div>
        <form aria-label="Item Zufügen">
          <Stack tokens={{ childrenGap: '0.3em' }} style={{ width: '30em' }}>
            <div>
              <TextField
                required
                label="Name"
                prefix={`${props.entity.name},`}
                value={`${name}`}
                onChange={((ev, data) => setName(data)) as any}
              />
            </div>
            <div>
              <SpinButton
                label="Anzahl der Kartons / Komponenten"
                min={1}
                max={10}
                step={1}
                value={`${kartons}`}
                onChange={onChange}
                onInput={onChange}
              />
            </div>
            <div>
              <SpinButton
                label="freie № suchen ab"
                min={10000}
                max={99999}
                step={1000}
                value={`${searchStart}`}
                onChange={((ev, data) => setSearchStart(Number(data))) as any}
              />
            </div>
            <Stack.Item align="end">
              <div>
                <PrimaryButton
                  // type="summit"
                  disabled={saving}
                  text="Item zufügen"
                  onClick={anlegenStarten}
                >
                  {saving ? <Spinner labelPosition="right" size={SpinnerSize.small} /> : null}
                </PrimaryButton>
              </div>
            </Stack.Item>
          </Stack>
        </form>
      </div>
      <div>
        Bisher gibt es folgende Items:
        <DisplayItems entity={props.entity} />
      </div>
    </Stack>
  )
}
